import React, { useState, useEffect } from "react";
import {
  Trash2,
  Plus,
  X,
  Upload,
  Image,
  Video,
  FileText,
  Edit2,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import Loader from "../../components/common/Loader";

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-[60] animate-slide-in">
      <div
        className={`flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl ${
          type === "success"
            ? "bg-gradient-to-r from-green-500 to-emerald-600"
            : "bg-gradient-to-r from-[#DA3335] to-[#c02527]"
        } text-white`}
      >
        {type === "success" ? (
          <CheckCircle className="w-6 h-6" />
        ) : (
          <AlertCircle className="w-6 h-6" />
        )}
        <p className="font-medium">{message}</p>
        <button onClick={onClose} className="ml-2 hover:opacity-75">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmDialog = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-xl font-bold text-[#1b0a0a] mb-4">
          Confirm Action
        </h3>
        <p className="text-[#3c3737] mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-[#3c3737] rounded-lg hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2 bg-[#DA3335] text-white rounded-lg hover:bg-[#c02527] transition-all shadow-md"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPortfolio = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentItem, setCurrentItem] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [itemTitle, setItemTitle] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Toast and Confirm Dialog states
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_UPLOAD_PRESET;
  const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_NAME;
  const API_URL = process.env.REACT_APP_API_URL;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const showConfirm = (message, onConfirm) => {
    setConfirmDialog({ message, onConfirm });
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/get-all`);
      const startTime = Date.now();
      if (response.data.success) {
        setCategories(response.data.data);
      }
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(1000 - elapsed, 0);

      setTimeout(() => setIsLoading(false), remaining);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleAddCategory = () => {
    setModalMode("create");
    setCategoryName("");
    setIsModalOpen(true);
  };

  const handleAddItem = (category) => {
    setModalMode("addItem");
    setCurrentCategory(category);
    setItemTitle("");
    setSelectedFiles([]);
    setPreviewUrls([]);
    setIsModalOpen(true);
  };

  // const handleEditItems = (category, item) => {
  //   setModalMode("editItem");
  //   setCurrentCategory(category);
  //   setCurrentItem(item);
  //   setItemTitle(item.title);
  //   setIsModalOpen(true);
  // };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCategoryName("");
    setItemTitle("");
    setSelectedFiles([]);
    setPreviewUrls([]);
    setIsUploading(false);
    setUploadProgress(0);
    setCurrentCategory(null);
    setCurrentItem(null);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter((file) => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      const isPdf = file.type === "application/pdf";
      const isValid = isImage || isVideo || isPdf;

      if (!isValid) {
        showToast(`${file.name} is not a valid file type.`, "error");
        return false;
      }

      if (isVideo && file.size > 50 * 1024 * 1024) {
        showToast(`${file.name} exceeds 50MB limit for videos.`, "error");
        return false;
      }

      if (isPdf && file.size > 25 * 1024 * 1024) {
        showToast(`${file.name} exceeds 25MB limit for PDFs.`, "error");
        return false;
      }

      return true;
    });

    const filesWithType = validFiles.map((file) => {
      let type = "image";
      if (file.type.startsWith("video/")) type = "video";
      else if (file.type === "application/pdf") type = "pdf";

      return {
        file,
        type,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      };
    });

    setSelectedFiles((prevFiles) => [...prevFiles, ...filesWithType]);

    filesWithType.forEach((fileObj) => {
      if (fileObj.type === "pdf") {
        setPreviewUrls((prev) => [
          ...prev,
          { type: "pdf", url: fileObj.file.name },
        ]);
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrls((prev) => [
            ...prev,
            { type: fileObj.type, url: reader.result },
          ]);
        };
        reader.readAsDataURL(fileObj.file);
      }
    });
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadToCloudinary = async (file, fileType) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);

    let resourceType = "image";
    if (fileType === "video") resourceType = "video";
    else if (fileType === "pdf") resourceType = "raw";

    formData.append("resource_type", resourceType);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Cloudinary upload failed: ${response.status}`);
      }

      const data = await response.json();
      return { url: data.secure_url, type: fileType };
    } catch (error) {
      console.error("Error uploading to Cloudinary:", error);
      throw error;
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();

    if (!categoryName.trim()) {
      showToast("Please enter a category name.", "error");
      return;
    }

    try {
      await axios.post(`${API_URL}/portfolio/create-category`, {
        categoryName,
      });

      showToast(`Category "${categoryName}" created successfully!`);
      handleCloseModal();
      fetchCategories();
    } catch (error) {
      console.error("Error creating category:", error);
      showToast("Error creating category. Please try again.", "error");
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();

    if (!itemTitle.trim()) {
      showToast("Please enter an item title.", "error");
      return;
    }

    if (selectedFiles.length === 0) {
      showToast("Please select at least one file to upload.", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const mediaArray = [];
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const uploadedMedia = await uploadToCloudinary(
          selectedFiles[i].file,
          selectedFiles[i].type
        );
        mediaArray.push({
          mediaType: uploadedMedia.type,
          url: uploadedMedia.url,
        });
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      await axios.post(`${API_URL}/portfolio/add-item/${currentCategory._id}`, {
        title: itemTitle,
        media: mediaArray,
      });

      showToast(`Item "${itemTitle}" added successfully!`);
      handleCloseModal();
      fetchCategories();
    } catch (error) {
      console.error("Error creating item:", error);
      showToast("Error creating item. Please try again.", "error");
      setIsUploading(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();

    if (!itemTitle.trim()) {
      showToast("Please enter an item title.", "error");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const mediaArray = [...currentItem.media];
      const totalFiles = selectedFiles.length;

      for (let i = 0; i < selectedFiles.length; i++) {
        const uploadedMedia = await uploadToCloudinary(
          selectedFiles[i].file,
          selectedFiles[i].type
        );
        mediaArray.push({
          mediaType: uploadedMedia.type,
          url: uploadedMedia.url,
        });
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }

      await axios.put(
        `${API_URL}/portfolio/edit-item/${currentCategory._id}/${currentItem._id}`,
        {
          title: itemTitle,
          media: mediaArray,
        }
      );

      showToast(`Item "${itemTitle}" updated successfully!`);
      handleCloseModal();
      fetchCategories();
    } catch (error) {
      console.error("Error editing item:", error);
      showToast("Error editing item. Please try again.", "error");
      setIsUploading(false);
    }
  };

  const handleDeleteItem = async (categoryId, itemId) => {
    showConfirm("Are you sure you want to delete this item?", async () => {
      try {
        await axios.delete(
          `${API_URL}/portfolio/delete-item/${categoryId}/${itemId}`
        );
        showToast("Item deleted successfully!");
        fetchCategories();
      } catch (error) {
        console.error("Error deleting item:", error);
        showToast("Error deleting item. Please try again.", "error");
      }
      setConfirmDialog(null);
    });
  };

  const handleDeleteCategory = async (categoryId) => {
    showConfirm("Are you sure you want to delete this category?", async () => {
      try {
        await axios.delete(
          `${API_URL}/portfolio/delete-category/${categoryId}`
        );
        showToast("Category deleted successfully!");
        fetchCategories();
      } catch (error) {
        console.error("Error deleting category:", error);
        showToast("Error deleting category. Please try again.", "error");
      }
      setConfirmDialog(null);
    });
  };

  const renderMediaPreview = (media) => {
    if (media.mediaType === "image") {
      return (
        <img
          src={media.url}
          alt="preview"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/200?text=Image+Error";
          }}
        />
      );
    } else if (media.mediaType === "video") {
      return (
        <div className="relative w-full h-full">
          <video
            src={media.url}
            className="w-full h-full object-cover"
            controls={false}
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
            <Video size={32} className="text-white" />
          </div>
        </div>
      );
    } else if (media.mediaType === "pdf") {
      return (
        <div className="w-full h-full flex items-center justify-center bg-red-50">
          <FileText size={40} className="text-[#DA3335]" />
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog(null)}
        />
      )}

      <div className="w-full mx-auto">
        <div className="mb-4 mt-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1b0a0a] mb-2">
                Portfolio Manager
              </h1>
            </div>
            <button
              className="bg-[#1b0a0a] text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 shadow-md hover:shadow-lg hover:bg-[#2d1818]"
              onClick={handleAddCategory}
            >
              <Plus size={20} />
              Create Category
            </button>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : error ? (
          <div className="col-span-full text-center py-16">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-2xl font-bold text-[#1b0a0a] mb-2">
              Error Loading Categories
            </h3>
            <p className="text-[#3c3737]">{error}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {categories.map((category) => (
              <div
                key={category._id}
                className="bg-white rounded-3xl shadow-2xl p-6 border-t-4 border-[#DA3335]"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-[#1b0a0a]">
                      {category.categoryName}
                    </h2>
                    <p className="text-sm text-[#3c3737]">
                      {category.portfolioItems.length}{" "}
                      {category.portfolioItems.length === 1 ? "item" : "items"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAddItem(category)}
                      className="bg-[#144982] text-white p-2 rounded-full hover:bg-[#0f3660] transition-all shadow-md"
                      title="Add Item"
                    >
                      <Plus size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category._id)}
                      className="bg-[#DA3335] text-white p-2 rounded-full hover:bg-[#c02527] transition-all shadow-md"
                      title="Delete Category"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {category?.portfolioItems?.length > 0 ? (
                  <div className="space-y-4">
                    {category?.portfolioItems?.map((item) => (
                      <div
                        key={item._id}
                        className="bg-gray-50 rounded-2xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="text-lg font-semibold text-[#1b0a0a]">
                            {item.title}
                          </h3>
                          <div className="flex gap-2">
                            {/* <button
                              onClick={() => handleEditItems(category, item)}
                              className="bg-[#144982] text-white p-2 rounded-full hover:bg-[#0f3660] transition-all shadow-md"
                              title="Edit Item"
                            >
                              <Edit2 size={16} />
                            </button> */}
                            <button
                              onClick={() =>
                                handleDeleteItem(category._id, item._id)
                              }
                              className="bg-[#DA3335] text-white p-2 rounded-full hover:bg-[#c02527] transition-all shadow-md"
                              title="Delete Item"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                          {item.media.map((media) => (
                            <div
                              key={media._id}
                              className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100 shadow-sm hover:shadow-md hover:border-[#DA3335] transition-all"
                            >
                              {renderMediaPreview(media)}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300">
                    <p className="text-[#3c3737] mb-3">
                      No items in this category
                    </p>
                    <button
                      onClick={() => handleAddItem(category)}
                      className="bg-[#144982] text-white px-4 py-2 rounded-lg text-sm hover:bg-[#0f3660] transition-all shadow-md"
                    >
                      Add First Item
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!isLoading && categories.length === 0 && (
          <div className="text-center py-16 bg-white rounded-3xl shadow-2xl border-t-4 border-[#DA3335]">
            <div className="text-6xl mb-4">📁</div>
            <h3 className="text-2xl font-bold text-[#1b0a0a] mb-2">
              No Categories Found
            </h3>
            <p className="text-[#3c3737] mb-6">
              Start by creating your first portfolio category
            </p>
            <button
              onClick={handleAddCategory}
              className="bg-[#1b0a0a] text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-all hover:bg-[#2d1818] shadow-md"
            >
              <Plus size={20} />
              Create Category
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b-4 border-[#DA3335] bg-gradient-to-r from-gray-50 to-white">
              <h2 className="text-2xl font-bold text-[#1b0a0a]">
                {modalMode === "create"
                  ? "Create New Category"
                  : modalMode === "addItem"
                  ? "Add Portfolio Item"
                  : "Edit Portfolio Item"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-[#3c3737] hover:text-[#1b0a0a] transition-colors"
                disabled={isUploading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {modalMode === "create" ? (
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-[#1b0a0a] mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    placeholder="e.g., Graphic Design, Interior Design"
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DA3335] focus:border-[#DA3335]"
                    disabled={isUploading}
                  />
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-[#1b0a0a] mb-2">
                      Item Title *
                    </label>
                    <input
                      type="text"
                      value={itemTitle}
                      onChange={(e) => setItemTitle(e.target.value)}
                      placeholder="e.g., Annual Day Celebration 2025"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#DA3335] focus:border-[#DA3335]"
                      disabled={isUploading}
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-[#1b0a0a] mb-3">
                      Add Media *
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#DA3335] transition-colors bg-gray-50">
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                        disabled={isUploading}
                      />
                      <label
                        htmlFor="file-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Image size={48} className="text-[#144982] mb-3" />
                        <span className="text-[#DA3335] font-semibold mb-1">
                          Click to upload
                        </span>
                        <span className="text-sm text-[#3c3737]">
                          or drag and drop media here
                        </span>
                        <span className="text-xs text-gray-400 mt-2">
                          Images: PNG, JPG, JPEG, GIF | Videos: MP4, WebM (Max
                          50MB) | PDF (Max 25MB)
                        </span>
                      </label>
                    </div>
                  </div>

                  {previewUrls.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-[#1b0a0a] mb-3">
                        Selected Media ({selectedFiles.length})
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {previewUrls.map((preview, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100 relative shadow-sm hover:border-[#DA3335] transition-colors">
                              {preview.type === "pdf" ? (
                                <div className="w-full h-full flex items-center justify-center bg-red-50">
                                  <FileText
                                    size={32}
                                    className="text-[#DA3335]"
                                  />
                                </div>
                              ) : preview.type === "video" ? (
                                <>
                                  <video
                                    src={preview.url}
                                    className="w-full h-full object-cover"
                                    controls={false}
                                    muted
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                                    <Video size={24} className="text-white" />
                                  </div>
                                </>
                              ) : (
                                <img
                                  src={preview.url}
                                  alt={`Preview ${index + 1}`}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                            {!isUploading && (
                              <button
                                onClick={() => handleRemoveFile(index)}
                                className="absolute -top-2 -right-2 bg-[#DA3335] text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-[#c02527]"
                              >
                                <X size={16} />
                              </button>
                            )}
                            <p className="text-xs text-[#3c3737] mt-1 truncate">
                              {selectedFiles[index]?.file?.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isUploading && (
                    <div className="mt-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-[#1b0a0a]">
                          Uploading...
                        </span>
                        <span className="text-sm text-[#3c3737]">
                          {uploadProgress}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-[#DA3335] to-[#144982] h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t-2 border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2 border-2 border-gray-300 text-[#3c3737] rounded-lg hover:bg-white transition-all"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={
                  modalMode === "create"
                    ? handleCreateCategory
                    : modalMode === "addItem"
                    ? handleCreateItem
                    : handleEditItem
                }
                disabled={
                  isUploading ||
                  (modalMode === "create" && !categoryName.trim()) ||
                  (modalMode !== "create" &&
                    (!itemTitle.trim() || selectedFiles.length === 0))
                }
                className="bg-[#1b0a0a] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#2d1818] shadow-md"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    {modalMode === "create"
                      ? "Create Category"
                      : modalMode === "addItem"
                      ? `Add Item (${selectedFiles.length})`
                      : `Update Item (${selectedFiles.length})`}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminPortfolio;
